import React from 'react';
import FAQSection from '../components/common/FAQSection';
import { Link } from 'react-router-dom';
import SEO from '../components/common/SEO';
import { FaAws, FaGithub, FaWindows } from 'react-icons/fa';
import {
  SiDatabricks,
  SiSnowflake,
  SiGooglebigquery,
  SiApacheairflow,
  SiCloudera,
} from 'react-icons/si';

function DataEngineerAgent() {
  const supportedPlatforms = [
    { name: 'Databricks', icon: <SiDatabricks size={18} color="#FF3621" /> },
    { name: 'Snowflake', icon: <SiSnowflake size={18} color="#29B5E8" /> },
    { name: 'BigQuery', icon: <SiGooglebigquery size={18} color="#4285F4" /> },
    { name: 'Redshift', icon: <FaAws size={18} color="#FF9900" /> },
    { name: 'Fabric', icon: <FaWindows size={18} color="#00A4EF" /> },
    { name: 'Cloudera', icon: <SiCloudera size={18} color="#F96702" /> },
  ];

  const orchestrationBackends = [
    { name: 'Databricks Jobs', icon: <SiDatabricks size={18} color="#FF3621" /> },
    { name: 'Snowflake Tasks', icon: <SiSnowflake size={18} color="#29B5E8" /> },
    { name: 'Airflow DAGs', icon: <SiApacheairflow size={18} color="#017CEE" /> },
    { name: 'Fabric Pipelines', icon: <FaWindows size={18} color="#00A4EF" /> },
  ];

  const supportedFrameworks = ['SQL', 'dbt', 'PySpark', 'Snowpark', 'DLT'];

  const workflowStages = [
    {
      eyebrow: 'Discover',
      title: 'Starts from what already exists',
      desc: 'The agent inspects existing pipelines, recent runs, retry patterns, notification conventions, and orchestration behavior before it proposes a new graph.',
    },
    {
      eyebrow: 'Analyze',
      title: 'Reads requirements, schema, and metrics together',
      desc: 'It combines business requirements with bronze, silver, gold dependencies and metric refresh cadence so orchestration is derived from the full system, not from one isolated job description.',
    },
    {
      eyebrow: 'Design',
      title: 'Builds a canonical pipeline graph',
      desc: 'It plans ingestion, transformation, metrics refresh, testing, notifications, step dependencies, idempotency strategy, retry policy, and step-level SLAs as one coordinated execution plan.',
    },
    {
      eyebrow: 'Generate',
      title: 'Compiles platform-specific configs',
      desc: 'It emits runnable definitions for Databricks Jobs, Snowflake Tasks, Airflow DAGs, and related execution backends while preserving a portable canonical plan underneath.',
    },
  ];

  const planningCapabilities = [
    {
      title: 'Deduplication and ingestion strategy',
      desc: 'It infers whether a source needs window-based deduplication, full refresh, incremental append, or CDC-aware handling before silver-layer design begins.',
      accent: 'linear-gradient(135deg, #0EA5E9 0%, #2563EB 100%)',
    },
    {
      title: 'Join-key and transformation planning',
      desc: 'It suggests likely join keys, table relationships, masking rules, SCD behavior, and transformation rules so downstream dbt or Spark logic matches the shape of the data.',
      accent: 'linear-gradient(135deg, #14B8A6 0%, #0F766E 100%)',
    },
    {
      title: 'Framework-aware generation',
      desc: 'It is built to reason across SQL, dbt, PySpark, Snowpark, and DLT patterns instead of pretending one pipeline style fits every platform.',
      accent: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
    },
    {
      title: 'Operational runbooks',
      desc: 'The output includes failure guidance, SLA risk handling, retry posture, and backfill procedures so teams do not stop at code generation.',
      accent: 'linear-gradient(135deg, #F59E0B 0%, #EA580C 100%)',
    },
  ];

  const deliveryArtifacts = [
    'Canonical pipeline graph',
    'Job or task configs',
    'Step-level retry policies',
    'Timeout and SLA definitions',
    'Backfill procedure',
    'Failure and on-call runbook',
    'Lineage-aware refresh ordering',
    'GitHub-ready delivery path',
  ];

  return (
    <>
      <SEO pathname="/agents/data-engineer" agentKey="DataEngineerAgent" />
      <main className="agent-page" style={{ paddingTop: '80px', minHeight: '100vh', background: '#f8fafc', color: 'var(--color-text-primary)' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px 20px 0' }}>
          <Link to="/" style={{ color: '#0071e3', textDecoration: 'none', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '0.95rem' }}>
            <span style={{ fontSize: '1.2rem' }}>←</span> Back to Home
        </Link>
      </div>

        <section style={{ padding: '100px 0 120px', background: '#020617', position: 'relative', overflow: 'hidden', color: 'white', fontFamily: '"General Sans", sans-serif' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 18% 20%, rgba(14,165,233,0.22) 0%, rgba(14,165,233,0) 34%), radial-gradient(circle at 82% 22%, rgba(59,130,246,0.18) 0%, rgba(59,130,246,0) 28%), linear-gradient(180deg, rgba(2,6,23,0.92) 0%, rgba(2,6,23,0.98) 100%)' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(2,6,23,0.96) 0%, rgba(2,6,23,0.9) 42%, rgba(2,6,23,0.8) 100%)' }} />

          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px', position: 'relative', zIndex: 2 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '64px', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ flex: '1.2 1 0%', minWidth: '320px' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', padding: '6px 16px', background: 'rgba(255,255,255,0.08)', color: 'white', border: '1px solid rgba(255,255,255,0.16)', borderRadius: '30px', fontSize: '0.85rem', fontWeight: 700, marginBottom: '24px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                  <span style={{ display: 'inline-block', width: '6px', height: '6px', background: '#38bdf8', borderRadius: '50%', marginRight: '8px', boxShadow: '#38bdf8 0 0 10px' }} />
                  Principal Data Engineering Agent
              </div>
                <h1 style={{ fontSize: 'clamp(3rem, 5vw, 4.3rem)', marginBottom: '24px', color: 'white', fontWeight: 800, lineHeight: 1.04, letterSpacing: '-0.04em', maxWidth: '780px' }}>
                  Design <span style={{ color: '#67e8f9' }}>real pipelines</span>, not just isolated jobs.
              </h1>
                <p style={{ fontSize: '1.22rem', color: '#cbd5e1', marginBottom: '20px', lineHeight: 1.72, maxWidth: '690px' }}>
                  This agent behaves like a senior platform data engineer. It discovers existing orchestration patterns, reads requirements alongside schema and metric dependencies, and turns that context into a canonical pipeline graph with runnable backend-specific configs.
              </p>
                <p style={{ fontSize: '1.04rem', color: '#94a3b8', marginBottom: '36px', lineHeight: 1.78, maxWidth: '720px' }}>
                  Instead of generating a generic DAG from a prompt, it plans ingestion, bronze-to-silver-to-gold execution order, metrics refresh cadence, testing, notifications, retries, backfills, and SLA handling for Databricks, Snowflake, Airflow, Fabric, BigQuery, Redshift, and Cloudera environments.
              </p>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '26px' }}>
                  <a href="https://app.eagentix.com/register?reason=Requesting%20access%20to%20Data%20Engineer%20Agent" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#38bdf8', color: '#082f49', padding: '16px 32px', borderRadius: '12px', fontWeight: 700, fontSize: '1.05rem', textDecoration: 'none', boxShadow: 'rgba(56,189,248,0.28) 0 10px 28px -12px' }}>
                    Request Access Now
                </a>
              </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {['Lineage-Aware', 'Idempotent by Design', 'Backfill-Ready', 'Runbook Included'].map((item) => (
                    <span key={item} style={{ padding: '10px 14px', borderRadius: '999px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#e2e8f0', fontSize: '0.84rem', fontWeight: 600 }}>
                      {item}
                  </span>
                  ))}
              </div>
            </div>

              <div style={{ flex: '1 1 0%', minWidth: '320px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <div style={{ background: 'rgba(255,255,255,0.04)', backdropFilter: 'blur(14px)', border: '1px solid rgba(255,255,255,0.1)', padding: '32px', borderRadius: '24px', boxShadow: '0 28px 50px -36px rgba(15,23,42,0.8)' }}>
                  <p style={{ fontSize: '0.75rem', fontWeight: 800, color: 'rgba(255,255,255,0.46)', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '24px', textAlign: 'center' }}>
                    Supported Platforms
                </p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px' }}>
                    {supportedPlatforms.map((item) => (
                      <div key={item.name} style={{ padding: '12px 14px', borderRadius: '16px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', color: '#e2e8f0', fontSize: '0.92rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {item.icon}
                        <span>{item.name}</span>
                    </div>
                    ))}
                </div>
              </div>

                <div style={{ background: 'rgba(255,255,255,0.04)', backdropFilter: 'blur(14px)', border: '1px solid rgba(255,255,255,0.1)', padding: '32px', borderRadius: '24px', boxShadow: '0 28px 50px -36px rgba(15,23,42,0.8)' }}>
                  <p style={{ fontSize: '0.75rem', fontWeight: 800, color: 'rgba(255,255,255,0.46)', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '18px', textAlign: 'center' }}>
                    Pipeline Compiler
                </p>
                  <p style={{ margin: '0 0 20px', color: '#cbd5e1', lineHeight: 1.7, fontSize: '0.98rem', textAlign: 'center' }}>
                    One canonical execution plan, then framework-aware generation and backend-specific delivery for the orchestration system your platform already trusts.
                </p>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '18px' }}>
                    {supportedFrameworks.map((item) => (
                      <span key={item} style={{ padding: '9px 12px', borderRadius: '999px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', color: '#e2e8f0', fontSize: '0.8rem', fontWeight: 700 }}>
                        {item}
                    </span>
                    ))}
                </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px', marginBottom: '18px' }}>
                    {orchestrationBackends.map((item) => (
                      <div key={item.name} style={{ padding: '12px 14px', borderRadius: '16px', background: 'linear-gradient(135deg, rgba(56,189,248,0.1), rgba(99,102,241,0.05))', border: '1px solid rgba(125,211,252,0.12)', color: '#e2e8f0', fontSize: '0.84rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center' }}>
                        {item.icon}
                        <span>{item.name}</span>
                    </div>
                    ))}
                </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', color: '#94a3b8', fontSize: '0.86rem', fontWeight: 600 }}>
                    <FaGithub size={16} color="#cbd5e1" />
                    Repo-aware release and PR-friendly delivery path
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

        <section style={{ padding: '72px 0', background: 'white', borderTop: '1px solid rgba(0,0,0,0.05)', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
            <div style={{ textAlign: 'center', marginBottom: '48px', maxWidth: '820px', marginLeft: 'auto', marginRight: 'auto' }}>
              <span className="section-label">Platform-Aware Pipeline Orchestration</span>
              <h2 style={{ fontSize: '2.5rem', color: '#1d1d1f', fontWeight: '800', letterSpacing: '-0.03em', margin: '16px 0' }}>
                From discovery to orchestration configs, modeled as one execution system
            </h2>
              <p style={{ fontSize: '1.1rem', color: 'var(--color-text-secondary)', lineHeight: '1.7', margin: 0 }}>
                The agent does not stop at generating a DAG skeleton. It discovers existing pipelines, reasons through medallion dependencies, chooses orchestration backends, documents retries and backfills, and prepares delivery artifacts your team can actually operate.
            </p>
          </div>

            <div style={{ borderRadius: '28px', overflow: 'hidden', background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 30px 60px -32px rgba(15,23,42,0.22)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.1fr) minmax(320px, 0.9fr)', gap: '0' }}>
                <div style={{ padding: '34px 34px 30px', borderRight: '1px solid rgba(148,163,184,0.14)' }}>
                  <p style={{ margin: 0, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.16em', color: '#0284c7', fontWeight: '800' }}>
                    Workflow Sequence
                </p>
                  <div style={{ display: 'grid', gap: '16px', marginTop: '22px' }}>
                    {workflowStages.map((stage, index) => (
                      <div key={stage.title} style={{ padding: '18px 18px 18px 20px', borderRadius: '20px', background: index === 0 ? 'linear-gradient(145deg, rgba(239,246,255,0.98) 0%, rgba(255,255,255,0.98) 100%)' : 'rgba(255,255,255,0.82)', border: '1px solid rgba(148,163,184,0.14)', boxShadow: '0 16px 30px -24px rgba(15,23,42,0.12)' }}>
                        <p style={{ margin: 0, fontSize: '0.73rem', textTransform: 'uppercase', letterSpacing: '0.14em', color: '#0369a1', fontWeight: '800' }}>
                          {String(index + 1).padStart(2, '0')} · {stage.eyebrow}
                      </p>
                        <h3 style={{ fontSize: '1.12rem', margin: '10px 0 8px', color: '#0f172a', fontWeight: '800', letterSpacing: '-0.02em' }}>
                          {stage.title}
                      </h3>
                        <p style={{ margin: 0, color: '#475569', lineHeight: '1.7', fontSize: '0.95rem' }}>
                          {stage.desc}
                      </p>
                    </div>
                    ))}
                </div>
              </div>

                <div style={{ padding: '34px 34px 30px', background: 'linear-gradient(145deg, #0f172a 0%, #111827 100%)', color: 'white' }}>
                  <p style={{ margin: 0, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.16em', color: '#7dd3fc', fontWeight: '800' }}>
                    Delivery Surface
                </p>
                  <h3 style={{ fontSize: '1.8rem', margin: '16px 0 12px', fontWeight: 800, letterSpacing: '-0.03em' }}>
                    What the pipeline plan actually produces
                </h3>
                  <p style={{ margin: '0 0 20px', color: '#94a3b8', lineHeight: '1.75', fontSize: '0.98rem' }}>
                    The backend already thinks in terms of schedules, retries, SLAs, backfills, failure playbooks, and platform-specific configs. This section makes that output legible at a glance.
                </p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px' }}>
                    {deliveryArtifacts.map((item) => (
                      <div key={item} style={{ padding: '14px 14px', borderRadius: '16px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', color: '#e2e8f0', fontSize: '0.84rem', fontWeight: 700, lineHeight: '1.45' }}>
                        {item}
                    </div>
                    ))}
                </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#cbd5e1', fontSize: '0.88rem', fontWeight: 700, marginTop: '18px' }}>
                    <FaGithub size={16} color="#e2e8f0" />
                    Ready for repo-aware release and PR packaging
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

        <section style={{ padding: '96px 0', background: 'radial-gradient(circle at top, rgba(186,230,253,0.22) 0%, rgba(255,255,255,0.96) 22%, #f8fafc 58%, #ffffff 100%)' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 0.95fr) minmax(0, 1.2fr)', gap: '28px', alignItems: 'start' }}>
              <div style={{ position: 'sticky', top: '110px' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: '8px 14px', borderRadius: '999px', background: 'rgba(255,255,255,0.84)', border: '1px solid rgba(148,163,184,0.22)', boxShadow: '0 16px 30px -22px rgba(15,23,42,0.18)' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '999px', background: '#0ea5e9', boxShadow: '0 0 18px rgba(14,165,233,0.55)' }} />
                  <span className="section-label" style={{ margin: 0 }}>Planning Intelligence</span>
              </div>
                <h2 style={{ fontSize: 'clamp(2.7rem, 5vw, 4.8rem)', color: '#0f172a', fontWeight: '900', letterSpacing: '-0.05em', lineHeight: '0.94', margin: '24px 0 18px' }}>
                  Pipeline planning with <span style={{ color: '#0284c7' }}>operational depth</span>
              </h2>
                <p style={{ fontSize: '1.12rem', color: '#475569', lineHeight: '1.82', marginBottom: '24px', maxWidth: '560px' }}>
                  The pipeline agent uses utility-driven planning for deduplication, incremental logic, join discovery, and transformation rules, then carries that thinking into orchestration, retries, alerts, and backfill design.
              </p>
                <div style={{ borderRadius: '28px', padding: '28px', background: 'linear-gradient(145deg, #0f172a 0%, #0c4a6e 100%)', color: 'white', boxShadow: '0 34px 64px -30px rgba(15,23,42,0.38)' }}>
                  <p style={{ margin: 0, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.16em', color: '#bae6fd', fontWeight: '800' }}>Why This Matters</p>
                  <p style={{ margin: '14px 0 0', color: '#dbeafe', lineHeight: '1.8', fontSize: '1.02rem' }}>
                    Good pipelines fail in production when retry posture, dependency ordering, observability, and backfill mechanics are treated as afterthoughts. This agent puts those concerns inside the design itself.
                </p>
              </div>
            </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '22px' }}>
                <div style={{ gridColumn: '1 / -1', borderRadius: '28px', padding: '30px', background: 'linear-gradient(145deg, rgba(255,255,255,0.96) 0%, rgba(239,246,255,0.98) 100%)', border: '1px solid rgba(14,165,233,0.12)', boxShadow: '0 24px 44px -30px rgba(15,23,42,0.18)', position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', top: '-40px', right: '-20px', width: '180px', height: '180px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(14,165,233,0.18) 0%, rgba(14,165,233,0) 70%)' }} />
                  <div style={{ position: 'relative', zIndex: 1 }}>
                    <div style={{ width: '58px', height: '58px', borderRadius: '18px', background: 'linear-gradient(135deg, #0EA5E9 0%, #2563EB 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '1.45rem', fontWeight: '800', boxShadow: '0 18px 34px -18px rgba(14,165,233,0.55)' }}>
                      P
                  </div>
                    <h3 style={{ fontSize: '1.55rem', margin: '18px 0 10px', color: '#0f172a', fontWeight: '800', letterSpacing: '-0.03em' }}>
                      From system intent to executable orchestration
                  </h3>
                    <p style={{ color: '#475569', lineHeight: '1.8', fontSize: '1rem', margin: 0 }}>
                      The strongest output is not a static DAG screenshot. It is a pipeline design that understands ingestion patterns, data contract dependencies, metric refresh order, job semantics, and operational guardrails before code lands in production.
                  </p>
                </div>
              </div>

                {planningCapabilities.map((capability) => (
                  <div key={capability.title} style={{ borderRadius: '24px', padding: '26px', background: 'rgba(255,255,255,0.82)', backdropFilter: 'blur(14px)', border: '1px solid rgba(148,163,184,0.14)', boxShadow: '0 20px 36px -30px rgba(15,23,42,0.18)', position: 'relative', overflow: 'hidden' }}>
                    <div style={{ position: 'absolute', inset: 0, background: capability.accent, opacity: 0.08, filter: 'blur(72px)', transform: 'translate(30%, -36%)' }} />
                    <div style={{ position: 'relative', zIndex: 1 }}>
                      <div style={{ width: '50px', height: '50px', borderRadius: '16px', background: capability.accent, boxShadow: '0 14px 28px -18px rgba(15,23,42,0.28)', marginBottom: '16px' }} />
                      <h3 style={{ fontSize: '1.18rem', marginBottom: '10px', color: '#0f172a', fontWeight: '800', letterSpacing: '-0.02em', lineHeight: '1.2' }}>
                        {capability.title}
                    </h3>
                      <p style={{ color: '#475569', lineHeight: '1.75', fontSize: '0.97rem', margin: 0 }}>
                        {capability.desc}
                    </p>
                  </div>
                </div>
                ))}
            </div>
          </div>
        </div>
      </section>

      <FAQSection agentKey="DataEngineerAgent" />
      </main>
    </>
  );
}

export default DataEngineerAgent;
